/**
 * The shared kit's own words.
 *
 * These are the labels a component ships with when the caller does not pass its
 * own: the table's footer line, a drawer's close button, the Excel import's
 * standing instructions. A feature that needs different wording still overrides
 * it through props — this is only the default.
 */

/* The counted noun agrees with its number: 3 to 10 take the plural, 11 to 99 the
   accusative singular, the rest the singular — "3 مدخل" read as a mistake. */
const entries = (n) => ({ few: 'مدخلات', many: 'مدخلا' })[new Intl.PluralRules('ar').select(n)] ?? 'مدخل'

/** dtLanguage — _AdminsBaseLayout.cshtml:1152-1167. */
export const table = {
  search: 'ابحث:',
  lengthBefore: 'أظهر',
  lengthAfter: 'مدخلات',
  zeroRecords: 'لا توجد مدخلات في النظام',
  /* A search that matched nothing — the list itself is not empty. */
  noMatch: 'لا توجد نتائج مطابقة للبحث',
  loadFailed: 'تعذر تحميل البيانات',
  processing: 'جار التحميل...',
  /* "1–10 من 176": the range and the total, which is all the footer has to say. The
     range is isolated left-to-right (U+2066…U+2069): in an RTL line "1–10" would
     otherwise be reordered to read "10–1". */
  info: (start, end, total) => `\u2066${start}–${end}\u2069 من ${total}`,
  infoEmpty: 'لا توجد سجلات',
  infoFiltered: (max) => `(من أصل ${max} ${entries(max)})`,
  perPage: 'لكل صفحة',
  previous: 'السابق',
  next: 'التالي',
  pagination: 'ترقيم الصفحات',
}

/** Words the kit reuses across many components. */
export const kit = {
  retry: 'إعادة المحاولة',
  /* An address that matches no page (Taiga #1547). */
  notFound: 'الصفحة غير موجودة',
  notFoundHint: 'الرابط الذي فتحته لا يقابل أي صفحة؛ ربما كتب بشكل خاطئ أو لم يعد متاحا.',
  goHome: 'العودة إلى الرئيسية',
  /* Read after a required field's label; the star beside it is hidden from screen readers. */
  fieldRequired: 'مطلوب',
  /* Beside the label of a field the form can be sent without — said once, rather than
     starring every field that IS required. */
  fieldOptional: 'اختياري',
  /* The clear button inside a field. */
  clearField: 'مسح',
  /* GroupPicker — shared by five forms, so its own wording lives with the kit. */
  pickerMore: (shown, total) => `عرض ${shown} من ${total} — حدد البحث للوصول إلى الباقي`,
  pickerChosen: 'إدارة مختارة',
  pickerEmpty: 'لا توجد إدارة بهذا الاسم',
  pickerKinds: 'نوع المجموعة',
  pickerAll: 'الكل',
  /* The count beside the picker's label: the figure, then what it is counted against. */
  pickerOf: (total) => `من ${total}`,
  more: 'المزيد',
  back: 'رجوع',
  nextStep: 'التالي',
  previousStep: 'السابق',
  close: 'إغلاق', // Resources.Close
  confirmDelete: 'تأكيد الحذف',
  /* Type-to-confirm, for a delete that cannot be undone — see ConfirmDialog. */
  confirmByTyping: 'للتأكيد قم بكتابة الاسم',
  copy: 'نسخ', // Resources.Copy
  copied: 'تم النسخ',
  delete: 'حذف', // Resources.Delete
  cancel: 'إلغاء', // Resources.Cancel
  /* FilterBar: takes every applied filter off at once. */
  clearAll: 'مسح الكل',
  export: 'تصدير', // Resources.Export
  options: 'خيارات',
  moreInfo: 'مزيد من المعلومات',
  clearSearch: 'مسح البحث',
  /* The filter inside a long dropdown — see Select. */
  selectSearch: 'ابحث…',
  selectNoMatches: 'لا توجد نتائج',
  clearDate: 'مسح التاريخ',
  calendar: 'التقويم',
  today: 'اليوم',
  chooseMonth: 'اختيار الشهر',
  /* DateRangeField */
  apply: 'تطبيق',
  rangePresets: 'فترات جاهزة',
  rangePreset: {
    today: 'اليوم',
    last7: 'آخر 7 أيام',
    last30: 'آخر 30 يوما',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisYear: 'هذا العام',
  },
  rangePickStart: 'اختر تاريخ البداية',
  rangePickEnd: 'اختر تاريخ النهاية',
  /* Arabic counts: يوم واحد، يومان، 3–10 أيام، 11 فما فوق يوما. */
  rangeDays: (n) => (n === 1 ? 'يوم واحد' : n === 2 ? 'يومان' : n <= 10 ? `${n} أيام` : `${n} يوما`),
  /* TimePicker: its own label, since the field beside it names the date, and the two
     column headings — 00–23 and 00–55 are the same shape until you read them. */
  time: 'الوقت',
  hours: 'الساعة',
  minutes: 'الدقيقة',
  remove: 'إزالة',
  chooseFile: 'اختيار ملف',
  noFile: 'لم يتم اختيار ملف',
  dropHint: 'أو اسحب الملف وأفلته هنا',
  removeFile: 'إزالة الملف',
  fileInPlace: 'الملف الحالي',
  currentFile: 'الحالي',
  newFile: 'الجديد',
  loading: 'يتم جلب البيانات من الخادم',
  /** The list separator — an Arabic comma here, a Latin one in English. */
  listSep: '، ',
  /** A chart with nothing to plot for the period chosen. */
  noData: 'لا توجد بيانات',
}

export const excel = {
  intro: 'يرجى رفع ملف بصيغة Excel (.xlsx) أو CSV (.csv)',
  /* The topic import is not an excel import at all - it takes a ZIP exported from
     another installation, and the server rejects anything else. */
  introZip: 'يرجى رفع ملف مضغوط بصيغة ZIP (.zip)',
  columnsIntro: 'يجب أن يحتوي الملف على الأعمدة التالية',
  mandatory: 'إجباري',
  optional: 'اختياري',
  notesTitle: 'ملاحظات مهمة',
  notes: 'حمل القالب أولا، وأبق أسماء الأعمدة وترتيبها كما هي في الملف.',
  notesZip: 'الملف المضغوط هو ما تصدره هذه الشاشة من تثبيت آخر؛ لا يوجد قالب لتحميله. الموضوع الذي يحمل اسما موجودا مسبقا لا يستورد.',
  template: 'تحميل القالب',
  save: 'رفع',
  close: 'إغلاق',
  fileRequired: 'فضلا اختر ملف إكسل',
}

/** `getStatusString`, _MessagesModal.cshtml:249-266. */
export const notificationStates = {
  Waiting: 'في الإنتظار',
  Running: 'جارية',
  Stopped: 'توقفت',
  Finished: 'إنتهت',
  Failed: 'فشلت',
  Succeeded: 'نجحت',
  Skipped: 'تم التجاوز: مكتمل مسبقاً',
}

export const notifications = {
  title: 'الإشعارات', // Resources.Notifications
  details: 'التفاصيل', // Resources.Details
  preview: 'عرض', // Resources.Preview
  state: 'الحالة', // Resources.Status
  scheduledOn: 'تاريخ الجدولة', // Resources.CreatedOn
  startDate: 'تاريخ البداية', // Resources.StartDate
  endDate: 'تاريخ النهاية', // Resources.EndDate
  recipient: 'المستلم',
  deliveryStatus: 'التسليم',
  tries: 'المحاولات',
  error: 'الخطأ',
  noMessages: 'لم ينتج عن هذه الدفعة أي رسائل.',
  loadFailed: 'تعذر تحميل سجل الإشعارات.',
}

/** The inline rich-text toolbar. */
export const editor = {
  bold: 'عريض',
  italic: 'مائل',
  underline: 'تسطير',
  heading: 'عنوان',
  bullets: 'قائمة',
  link: 'رابط',
  linkPrompt: 'الرابط',
  clear: 'إزالة التنسيق',
}

/** The light/dark control. `الوضع` is the legacy header's own word for it. */
export const theme = {
  label: 'الوضع',
  light: 'فاتح',
  dark: 'داكن',
}

/**
 * What the TRANSPORT says when it has to speak for itself.
 *
 * `src/lib/{api,upload,download}.js` raise these — a network failure, a redirect into HTML,
 * a lapsed session, a refused role, a bare HTTP status. They are NOT server messages, so
 * the "show it as received" rule (W6) does not cover them: they were hard-coded English and
 * an Arabic panel showed `Request failed (502)` on any outage.
 */
export const transport = {
  network: 'تعذر الاتصال بالخادم، تحقق من الاتصال ثم أعد المحاولة',
  networkUpload: 'تعذر الاتصال بالخادم أثناء رفع الملف',
  redirected: 'تعذر إتمام الطلب، فضلا أعد تحميل الصفحة',
  sessionEnded: 'انتهت الجلسة، فضلا سجل الدخول مرة أخرى',
  denied: 'لا تملك الصلاحيات لتنفيذ هذه العملية',
  deniedDownload: 'لا تملك الصلاحيات لتحميل هذا الملف',
  html: 'تعذرت قراءة رد الخادم، فضلا أعد المحاولة',
  /** `(502)` — the code stays, it is what a support call is opened with. */
  failed: (status) => `تعذر إتمام الطلب (${status})`,
  uploadFailed: (status) => `تعذر رفع الملف (${status})`,
}

/**
 * The page-level activation switch's outcome, said the same way on every page
 * that carries one.
 *
 * These are UI chrome, not server copy. Four pages each toasted (or did not
 * toast) their own wording, so activating surveys and activating policies
 * announced themselves differently — or, on three of the four, not at all. The
 * SERVER's message is still what a FAILURE says, because only the server knows
 * why; success is a state the UI already knows it asked for.
 */
export const feature = {
  enabled: 'تم تفعيل الخاصية',
  disabled: 'تم إيقاف الخاصية',
  failed: 'تعذر تنفيذ العملية',
}
